type FullscreenCapableElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  webkitRequestFullScreen?: () => Promise<void> | void;
  mozRequestFullScreen?: () => Promise<void> | void;
  msRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenCapableDocument = Document & {
  webkitExitFullscreen?: () => Promise<void> | void;
  webkitCancelFullScreen?: () => Promise<void> | void;
  mozCancelFullScreen?: () => Promise<void> | void;
  msExitFullscreen?: () => Promise<void> | void;
  webkitFullscreenElement?: Element | null;
  webkitCurrentFullScreenElement?: Element | null;
  webkitIsFullScreen?: boolean;
  mozFullScreenElement?: Element | null;
  msFullscreenElement?: Element | null;
};

let transientFullscreenExitDeadline = 0;

export const isPageFullscreen = (): boolean => {
  if (typeof document === 'undefined') {
    return false;
  }

  const fullscreenDocument = document as FullscreenCapableDocument;

  return Boolean(
    fullscreenDocument.fullscreenElement ||
      fullscreenDocument.webkitFullscreenElement ||
      fullscreenDocument.webkitCurrentFullScreenElement ||
      fullscreenDocument.webkitIsFullScreen ||
      fullscreenDocument.mozFullScreenElement ||
      fullscreenDocument.msFullscreenElement,
  );
};

export const suppressNextFullscreenAutoClose = (durationMs = 5000): void => {
  transientFullscreenExitDeadline = Date.now() + durationMs;
};

export const consumeTransientFullscreenExit = (): boolean => {
  if (Date.now() > transientFullscreenExitDeadline) {
    transientFullscreenExitDeadline = 0;
    return false;
  }

  transientFullscreenExitDeadline = 0;
  return true;
};

export const requestPageFullscreen = async (): Promise<boolean> => {
  if (typeof document === 'undefined') {
    return false;
  }

  const fullscreenDocument = document as FullscreenCapableDocument;

  if (isPageFullscreen()) {
    return true;
  }

  const rootElement = document.documentElement as FullscreenCapableElement | null;

  if (!rootElement) {
    return false;
  }

  try {
    if (rootElement.requestFullscreen) {
      try {
        await rootElement.requestFullscreen({ navigationUI: 'hide' });
        return true;
      } catch {
        await rootElement.requestFullscreen();
        return true;
      }
    }

    if (rootElement.webkitRequestFullscreen) {
      await rootElement.webkitRequestFullscreen();
      return true;
    }

    if (rootElement.webkitRequestFullScreen) {
      await rootElement.webkitRequestFullScreen();
      return true;
    }

    if (rootElement.mozRequestFullScreen) {
      await rootElement.mozRequestFullScreen();
      return true;
    }

    if (rootElement.msRequestFullscreen) {
      await rootElement.msRequestFullscreen();
      return true;
    }
  } catch {
    return false;
  }

  return false;
};

export const exitPageFullscreen = async (): Promise<void> => {
  if (typeof document === 'undefined') {
    return;
  }

  const fullscreenDocument = document as FullscreenCapableDocument;

  try {
    if (fullscreenDocument.exitFullscreen) {
      await fullscreenDocument.exitFullscreen();
      return;
    }

    if (fullscreenDocument.webkitExitFullscreen) {
      await fullscreenDocument.webkitExitFullscreen();
      return;
    }

    if (fullscreenDocument.webkitCancelFullScreen) {
      await fullscreenDocument.webkitCancelFullScreen();
      return;
    }

    if (fullscreenDocument.mozCancelFullScreen) {
      await fullscreenDocument.mozCancelFullScreen();
      return;
    }

    if (fullscreenDocument.msExitFullscreen) {
      await fullscreenDocument.msExitFullscreen();
    }
  } catch {
    // Ignore fullscreen exit failures so modal close is never blocked.
  }
};
